import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  useColorModeValue,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Wrap,
  WrapItem,
  Divider,
  ButtonGroup,
} from '@chakra-ui/react';
import {
  FiSmile,
  FiX,
  FiStar,
  FiHeart,
  FiFrown,
  FiMeh,
} from 'react-icons/fi';
import axios from 'axios';
import env from 'config/env';
import { useEncryption } from '../../../contexts/EncryptionContext';

interface Mood {
  label: string;
  icon: React.ReactElement;
  color: string;
}

interface MoodValue {
  label: string;
  encrypted?: string;
}

const DEFAULT_MOODS: Mood[] = [
  { label: 'Amazing', icon: <FiStar />, color: 'yellow.400' },
  { label: 'Happy', icon: <FiSmile />, color: 'green.400' },
  { label: 'Good', icon: <FiHeart />, color: 'blue.400' },
  { label: 'Okay', icon: <FiMeh />, color: 'purple.400' },
  { label: 'Down', icon: <FiFrown />, color: 'red.400' },
];

interface MoodSelectorProps {
  value?: MoodValue;
  onChange: (mood: MoodValue) => void;
  detectButton?: React.ReactNode;
}

const MoodSelector: React.FC<MoodSelectorProps> = ({ value, onChange, detectButton }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [customMoods, setCustomMoods] = useState<{ encrypted: string; decrypted: string }[]>([]);
  const [filteredMoods, setFilteredMoods] = useState<{ encrypted: string; decrypted: string }[]>([]);
  const { decrypt } = useEncryption();
  const moodHoverLight = (color: string) => `${color}50`;
  const moodHoverDark = (color: string) => `${color}900`;
  const isLight = useColorModeValue(true, false);

  useEffect(() => {
    // Fetch past custom moods from the server
    const fetchCustomMoods = async () => {
      try {
        const response = await axios.get(`${env.API_URL}/moods`);
        const decryptedMoods = await Promise.all(
          response.data.map(async (encrypted: string) => ({
            encrypted,
            decrypted: await decrypt(encrypted)
          }))
        );
        setCustomMoods(decryptedMoods);
      } catch (error) {
        console.error('Error fetching custom moods:', error);
      }
    };
    fetchCustomMoods();
  }, [decrypt]);

  useEffect(() => {
    if (searchQuery) {
      const filtered = customMoods.filter(mood =>
        mood.decrypted.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredMoods(filtered);
    } else {
      setFilteredMoods([]);
    }
  }, [searchQuery, customMoods]);

  const handleMoodSelect = (mood: Mood) => {
    onChange({ label: mood.label, encrypted: undefined });
    setIsOpen(false);
  };

  const handleCustomMoodSelect = (encrypted: string, decrypted: string) => {
    onChange({ label: decrypted, encrypted });
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      handleCustomMoodSelect(searchQuery.trim(), searchQuery.trim());
    }
  };

  const getSelectedMoodColor = () => {
    if (!value) return 'gray.400';
    const defaultMood = DEFAULT_MOODS.find(m => m.label === value.label);
    return defaultMood?.color || 'blue.400';
  };

  return (
    <Box width="300px">
      <Popover
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        placement="bottom-start"
      >
        <PopoverTrigger>
          <ButtonGroup size="md" width="full" isAttached variant="outline">
            <Button
              onClick={() => setIsOpen(true)}
              leftIcon={DEFAULT_MOODS.find(m => m.label === value?.label)?.icon}
              flex="1"
              justifyContent="flex-start"
              color={getSelectedMoodColor()}
              borderRight={detectButton ? "1px solid" : undefined}
              borderRightColor={useColorModeValue("gray.200", "gray.600")}
            >
              {value ? value.label : 'How are you feeling?'}
            </Button>
            {detectButton}
          </ButtonGroup>
        </PopoverTrigger>
        <PopoverContent width="300px" p={2}>
          <PopoverBody>
            <VStack spacing={4} align="stretch">
              <Wrap spacing={2}>
                {DEFAULT_MOODS.map((mood) => (
                  <WrapItem key={mood.label}>
                    <Button
                      size="sm"
                      variant={value?.label === mood.label ? 'solid' : 'ghost'}
                      leftIcon={mood.icon}
                      onClick={() => handleMoodSelect(mood)}
                      color={mood.color}
                      _hover={{ bg: isLight ? moodHoverLight(mood.color) : moodHoverDark(mood.color) }}
                    >
                      {mood.label}
                    </Button>
                  </WrapItem>
                ))}
              </Wrap>

              <Divider />

              <Box>
                <InputGroup size="md">
                  <Input
                    placeholder="Search or add custom mood..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearchSubmit();
                      }
                    }}
                  />
                  {searchQuery && (
                    <InputRightElement>
                      <IconButton
                        aria-label="Clear search"
                        icon={<FiX />}
                        size="sm"
                        variant="ghost"
                        onClick={() => setSearchQuery('')}
                      />
                    </InputRightElement>
                  )}
                </InputGroup>

                {filteredMoods.length > 0 && (
                  <VStack mt={2} align="stretch" maxH="200px" overflowY="auto">
                    {filteredMoods.map((mood) => (
                      <Button
                        key={mood.encrypted}
                        size="sm"
                        variant="ghost"
                        justifyContent="flex-start"
                        onClick={() => handleCustomMoodSelect(mood.encrypted, mood.decrypted)}
                      >
                        {mood.decrypted}
                      </Button>
                    ))}
                  </VStack>
                )}
              </Box>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export default MoodSelector; 